import { lazy, Suspense } from "react";
import { RouteAnnouncer } from "./components/RouteAnnouncer";
import type { ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import Layout from "./components/Layout";
import { Loading } from "./components/States";
import { PortalCurrencyProvider } from "./portal/PortalCurrencyContext";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const DesignSystemPage = import.meta.env.DEV
  ? lazy(() => import("./pages/DesignSystemPage"))
  : null;
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const BrandsPage = lazy(() => import("./pages/BrandsPage"));
const UnitsPage = lazy(() => import("./pages/UnitsPage"));
const WarehousesPage = lazy(() => import("./pages/WarehousesPage"));
const BranchesPage = lazy(() => import("./pages/BranchesPage"));
const CRMShell = lazy(() => import("./pages/crm/CRMShell"));
const CRMDashboard = lazy(() => import("./pages/crm/CRMDashboard"));
const CrmLeadsPage = lazy(() => import("./pages/crm/LeadsPage"));
const CrmPipelinePage = lazy(() => import("./pages/crm/PipelinePage"));
const CrmInstallersPage = lazy(() => import("./pages/crm/InstallersPage"));
const CrmTasksBoardPage = lazy(() => import("./pages/crm/TasksBoardPage"));
const CrmCalendarPage = lazy(() => import("./pages/crm/CalendarPage"));
const CrmOffersPage = lazy(() => import("./pages/crm/OffersPage"));
const CrmServiceRequestsPage = lazy(() => import("./pages/crm/ServiceRequestsPage"));
const DealersPage = lazy(() => import("./pages/DealersPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage"));
const DealerDetailPage = lazy(() => import("./pages/DealerDetailPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const LoansPage = lazy(() => import("./pages/LoansPage"));
const AIPanelPage = lazy(() => import("./pages/AIPanelPage"));
const KnowledgeCenterPage = lazy(() => import("./pages/KnowledgeCenterPage"));
const DailyReportPage = lazy(() => import("./pages/DailyReportPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const SaleDetailPage = lazy(() => import("./pages/SaleDetailPage"));
const StockMovementsPage = lazy(() => import("./pages/StockMovementsPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const SuppliersInvestorsPage = lazy(() => import("./pages/SuppliersInvestorsPage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const RoleManagementPage = lazy(() => import("./pages/RoleManagementPage"));
const CleanupDemoPage = lazy(() => import("./pages/CleanupDemoPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PriceTypesPage = lazy(() => import("./pages/PriceTypesPage"));
const PrintBarcodePage = lazy(() => import("./pages/PrintBarcodePage"));
const SerialVariantVerificationPage = lazy(() => import("./pages/SerialVariantVerificationPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const ClientReturnsPage = lazy(() => import("./pages/ClientReturnsPage"));
const ContractsPage = lazy(() => import("./pages/ContractsPage"));
const PurchasesPage = lazy(() => import("./pages/PurchasesPage"));
const ImportWorkspacePage = lazy(() => import("./pages/ImportWorkspacePage"));
const PurchaseOrdersPage = lazy(() => import("./pages/PurchaseOrdersPage"));
const SupplierReturnsPage = lazy(() => import("./pages/SupplierReturnsPage"));
const SupplierContractsPage = lazy(() => import("./pages/SupplierContractsPage"));
const PaymentReceivedPage = lazy(() => import("./pages/PaymentReceivedPage"));
const PaymentMadePage = lazy(() => import("./pages/PaymentMadePage"));
const AdminPromotionsPage = lazy(() => import("./pages/AdminPromotionsPage"));
const AdminPointsPage = lazy(() => import("./pages/AdminPointsPage"));
const AdminOrdersPage = lazy(() => import("./pages/AdminOrdersPage"));
const DealsPage = lazy(() => import("./pages/DealsPage"));
const PosPage = lazy(() => import("./pages/PosPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const FilialTransfersPage = lazy(() => import("./pages/FilialTransfersPage"));
const HRPage = lazy(() => import("./pages/HRPage"));

const PortalLoginPage = lazy(() => import("./portal/PortalLoginPage"));
const PortalRegisterPage = lazy(() => import("./portal/PortalRegisterPage"));
const PortalLayout = lazy(() => import("./portal/PortalLayout"));
const PortalCatalogPage = lazy(() => import("./portal/PortalCatalogPage"));
const PortalCartPage = lazy(() => import("./portal/PortalCartPage"));
const PortalOrdersPage = lazy(() => import("./portal/PortalOrdersPage"));
const PortalOrderDetailPage = lazy(() => import("./portal/PortalOrderDetailPage"));
const PortalPointsPage = lazy(() => import("./portal/PortalPointsPage"));
const PortalProfilePage = lazy(() => import("./portal/PortalProfilePage"));
const PortalDashboardPage = lazy(() => import("./portal/PortalDashboardPage"));
const PortalDebtPage = lazy(() => import("./portal/PortalDebtPage"));
const PortalSellerScanPage = lazy(() => import("./portal/PortalSellerScanPage"));
const InstallerPortalPage = lazy(() => import("./portal/InstallerPortalPage"));

const UstaLayout = lazy(() => import("./usta/UstaLayout"));
const UstaLoginPage = lazy(() => import("./usta/UstaLoginPage"));
const UstaDashboardPage = lazy(() => import("./usta/UstaDashboardPage"));
const UstaCatalogPage = lazy(() => import("./usta/UstaCatalogPage"));
const UstaTasksPage = lazy(() => import("./usta/UstaTasksPage"));
const UstaPointsPage = lazy(() => import("./usta/UstaPointsPage"));
const UstaProfilePage = lazy(() => import("./usta/UstaProfilePage"));

function RequireAuth({ children }: { children: ReactNode }) {
  const { token, user, isReady } = useAuth();
  if (!isReady) return null;
  if (!token) return <Navigate to="/login" replace />;
  if (user?.first_login_password_change_required) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

function RequirePasswordChangeAuth({ children }: { children: ReactNode }) {
  const { token, isReady } = useAuth();
  if (!isReady) return null;
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <PortalCurrencyProvider>
      <Suspense fallback={<Loading label="Саҳифа юкланмоқда..." />}>
        <RouteAnnouncer />
        <Routes>
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/register" element={<PortalRegisterPage />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<Navigate to="/portal/dashboard" replace />} />
            <Route path="dashboard" element={<PortalDashboardPage />} />
            <Route path="invoices" element={<PortalDashboardPage />} />
            <Route path="payments" element={<PortalDashboardPage />} />
            <Route path="bonus" element={<PortalDashboardPage />} />
            <Route path="cashback" element={<PortalDashboardPage />} />
            <Route path="shipments" element={<PortalDashboardPage />} />
            <Route path="warranty" element={<PortalDashboardPage />} />
            <Route path="support" element={<PortalDashboardPage />} />
            <Route path="documents" element={<PortalDashboardPage />} />
            <Route path="timeline" element={<PortalDashboardPage />} />
            <Route path="ai" element={<PortalDashboardPage />} />
            {import.meta.env.DEV && DesignSystemPage && <Route path="design" element={<DesignSystemPage />} />}
            <Route path="catalog" element={<PortalCatalogPage />} />
            <Route path="cart" element={<PortalCartPage />} />
            <Route path="orders" element={<PortalOrdersPage />} />
            <Route path="orders/:id" element={<PortalOrderDetailPage />} />
            <Route path="tasks" element={<InstallerPortalPage />} />
            <Route path="installer" element={<InstallerPortalPage />} />
            <Route path="jobs" element={<InstallerPortalPage />} />
            <Route path="installer/kpi" element={<InstallerPortalPage />} />
            <Route path="points" element={<PortalPointsPage />} />
            <Route path="scan" element={<PortalSellerScanPage />} />
            <Route path="profile" element={<PortalProfilePage />} />
            <Route path="debt" element={<PortalDebtPage />} />
            <Route path="*" element={<Navigate to="/portal/catalog" replace />} />
          </Route>

          <Route path="/usta/login" element={<UstaLoginPage />} />
          <Route path="/usta" element={<UstaLayout />}>
            <Route index element={<Navigate to="/usta/dashboard" replace />} />
            <Route path="dashboard" element={<UstaDashboardPage />} />
            <Route path="catalog" element={<UstaCatalogPage />} />
            <Route path="tasks" element={<UstaTasksPage />} />
            <Route path="points" element={<UstaPointsPage />} />
            <Route path="profile" element={<UstaProfilePage />} />
            <Route path="*" element={<Navigate to="/usta/dashboard" replace />} />
          </Route>

          <Route path="/login" element={<LoginPage />} />
          <Route path="/change-password" element={<RequirePasswordChangeAuth><ChangePasswordPage /></RequirePasswordChangeAuth>} />
          <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="daily-report" element={<DailyReportPage />} />
            <Route path="sales/:id" element={<SaleDetailPage />} />
            <Route path="stock-movements" element={<StockMovementsPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="suppliers-investors" element={<SuppliersInvestorsPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="roles" element={<RoleManagementPage />} />
            <Route path="cleanup" element={<CleanupDemoPage />} />
            <Route path="promotions" element={<AdminPromotionsPage />} />
            <Route path="points" element={<AdminPointsPage />} />
            <Route path="portal-orders" element={<AdminOrdersPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/:id?" element={<ProductDetailPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="brands" element={<BrandsPage />} />
            <Route path="units" element={<UnitsPage />} />
            <Route path="warehouses" element={<WarehousesPage />} />
            <Route path="branches" element={<BranchesPage />} />
            <Route path="crm" element={<CRMShell />}>
              <Route index element={<CRMDashboard />} />
              <Route path="leads" element={<CrmLeadsPage />} />
              <Route path="pipeline" element={<CrmPipelinePage />} />
              <Route path="installers" element={<CrmInstallersPage />} />
              <Route path="tasks" element={<CrmTasksBoardPage />} />
              <Route path="calendar" element={<CrmCalendarPage />} />
              <Route path="offers" element={<CrmOffersPage />} />
              <Route path="service-requests" element={<CrmServiceRequestsPage />} />
              <Route path="deals" element={<DealsPage />} />
            </Route>
            <Route path="crm/dashboard" element={<Navigate to="/crm" replace />} />
            <Route path="crm/customers" element={<Navigate to="/customers" replace />} />
            <Route path="crm/companies" element={<Navigate to="/customers" replace />} />
            <Route path="crm/dealers" element={<Navigate to="/dealers" replace />} />
            <Route path="crm/opportunities" element={<Navigate to="/crm/pipeline" replace />} />
            <Route path="crm/quotations" element={<Navigate to="/crm/offers" replace />} />
            <Route path="crm/sales-history" element={<Navigate to="/reports" replace />} />
            <Route path="crm/sales" element={<Navigate to="/reports" replace />} />
            <Route path="crm/service-history" element={<Navigate to="/crm/service-requests" replace />} />
            <Route path="crm/service" element={<Navigate to="/crm/service-requests" replace />} />
            <Route path="crm/debt-history" element={<Navigate to="/reports" replace />} />
            <Route path="crm/debt" element={<Navigate to="/reports" replace />} />
            <Route path="crm/documents" element={<Navigate to="/contracts" replace />} />
            <Route path="crm/timeline" element={<Navigate to="/crm/calendar" replace />} />
            <Route path="crm/ai-summary" element={<Navigate to="/crm" replace />} />
            <Route path="crm/ai" element={<Navigate to="/crm" replace />} />
            <Route element={<CRMShell />}>
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="dealers" element={<DealersPage />} />
              <Route path="dealers/:id" element={<DealerDetailPage />} />
              <Route path="contracts" element={<ContractsPage />} />
            </Route>
            <Route path="sales" element={<SalesPage />} />
            <Route path="sales/new" element={<Navigate to="/sales?create=1" replace />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="accounting" element={<FinancePage />} />
            <Route path="loans" element={<LoansPage />} />
            <Route path="hr" element={<HRPage />} />
            <Route path="hr/employees" element={<HRPage />} />
            <Route path="employees" element={<HRPage />} />
            <Route path="hr/departments" element={<HRPage mode="departments" />} />
            <Route path="hr/positions" element={<HRPage mode="positions" />} />
            <Route path="hr/payroll" element={<HRPage mode="payroll" />} />
            <Route path="hr/kpi" element={<HRPage mode="kpi" />} />
            <Route path="hr/attendance" element={<HRPage mode="attendance" />} />
            <Route path="ai" element={<AIPanelPage />} />
            <Route path="knowledge" element={<KnowledgeCenterPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="price-types" element={<PriceTypesPage />} />
            <Route path="print-barcode" element={<PrintBarcodePage />} />
            <Route path="serial-variant-verification" element={<SerialVariantVerificationPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="returns" element={<ReturnsPage />} />
            <Route path="client-returns" element={<ClientReturnsPage />} />
            <Route path="purchases" element={<PurchasesPage />} />
            <Route path="imports" element={<ImportWorkspacePage />} />
            <Route path="import" element={<ImportWorkspacePage />} />
            <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="supplier-returns" element={<SupplierReturnsPage />} />
            <Route path="supplier-contracts" element={<SupplierContractsPage />} />
            <Route path="payment-received" element={<PaymentReceivedPage />} />
            <Route path="payment-made" element={<PaymentMadePage />} />
            <Route path="pos" element={<PosPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="filial-transfers" element={<FilialTransfersPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </PortalCurrencyProvider>
  );
}
